import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import TagIcon from '@mui/icons-material/Tag';
import DirectionsIcon from '@mui/icons-material/Directions';
import { What3WordsLink } from '../../../../../shared/components/atoms/What3WordsLink';
import ConfirmDialog from '../../../../../shared/components/ui/ConfirmDialog';

interface SiteCardHeaderProps {
  name: string;
  slug?: string;
  what3WordsAddress?: string;
  latitude?: number | null;
  longitude?: number | null;
  /** When true, directions icon sits right next to the w3w address. When false (default), it's pushed to the far right. */
  compactDirections?: boolean;
  actions?: React.ReactNode;
}

export const SiteCardHeader: React.FC<SiteCardHeaderProps> = ({
  name,
  slug,
  what3WordsAddress,
  latitude,
  longitude,
  compactDirections = false,
  actions,
}) => {
  const [directionsDialogOpen, setDirectionsDialogOpen] = useState(false);

  const hasCoordinates = latitude != null && longitude != null;

  const handleOpenDirections = () => {
    window.open(
      `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`,
      '_blank',
      'noopener,noreferrer'
    );
    setDirectionsDialogOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        {/* Left side: Site name and location info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            flex: 1,
            minWidth: 0, // Allows text truncation to work properly
          }}
        >
          <Typography
            variant="h6"
            data-testid="site-name-header"
            sx={{
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </Typography>
          {slug && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
              data-testid="site-slug"
            >
              <TagIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {slug}
              </Typography>
            </Box>
          )}
          {what3WordsAddress && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: compactDirections ? undefined : 'space-between',
                gap: compactDirections ? 0.5 : undefined,
              }}
            >
              <What3WordsLink
                address={what3WordsAddress}
                testId="site-location"
              />
              {hasCoordinates && (
                <Tooltip title="Get Directions">
                  <IconButton
                    size="small"
                    data-testid="get-directions-button"
                    onClick={e => {
                      e.stopPropagation();
                      setDirectionsDialogOpen(true);
                    }}
                    sx={{ color: 'primary.main' }}
                  >
                    <DirectionsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        {/* Right side: Action buttons */}
        {actions && (
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>{actions}</Box>
        )}
      </Box>

      {hasCoordinates && (
        <ConfirmDialog
          open={directionsDialogOpen}
          title="Open Directions"
          message="This will open Google Maps in a new tab. Do you want to continue?"
          confirmText="Open"
          cancelText="Cancel"
          onConfirm={handleOpenDirections}
          onCancel={() => setDirectionsDialogOpen(false)}
        />
      )}
    </>
  );
};
